/* ManuscriptContextMenu.jsx
 * Right-click context menu for the manuscript prose area.
 * Shows clipboard actions (Cut / Copy / Paste) and a short set of
 * tool shortcuts so authors never have to leave the editor.
 *
 * Usage: render as a sibling of the prose container and pass the
 * shared scrollAreaRef so the click-outside handler can hit-test
 * against the menu portal.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Scissors, Copy, Clipboard, Search, Sparkles, Zap, BarChart2, X,
  MessageSquare, Mic2, Trash2, CheckSquare, BookText, ChevronRight, Check,
  RefreshCw, ScanSearch, Users, Map, Wand2,
} from 'lucide-react';
import { toast } from 'sonner';
import { usePW } from '@/lib/store';
import { cn } from '@/lib/utils';
import { useContextRebuild } from '@/hooks/useContextRebuild';

const MENU_WIDTH = 240;

// ── helpers ──────────────────────────────────────────────────────────────────
function hasSelection() {
  const sel = window.getSelection();
  return sel && sel.toString().trim().length > 0;
}

function clampX(x) {
  return Math.min(x, window.innerWidth - MENU_WIDTH - 8);
}
function clampY(y, menuH) {
  return Math.min(y, window.innerHeight - menuH - 8);
}

// ── component ─────────────────────────────────────────────────────────────────
export function ManuscriptContextMenu({ scrollAreaRef, askToneShift }) {
  const { activeChapterId, chapters, setActiveChapterId, project } = usePW();
  const { rebuild } = useContextRebuild();
  const [menu, setMenu] = useState(null); // { x, y, hasText }
  const menuRef = useRef(null);
  const [showChapters, setShowChapters] = useState(false);
  const [chapterSearch, setChapterSearch] = useState('');

  // Intercept right-click inside the manuscript scroll area only
  useEffect(() => {
    const area = scrollAreaRef?.current;
    if (!area) return;
    const onContext = (ev) => {
      // Only fire inside prose text nodes / editable paragraphs
      const target = ev.target;
      const insideProse =
        target.contentEditable === 'true' ||
        target.closest('[contenteditable="true"]') ||
        target.closest('[data-testid="manuscript-scroll-area"]');
      if (!insideProse) return;

      ev.preventDefault();
      setMenu({ x: clampX(ev.clientX), y: ev.clientY, hasText: hasSelection() });
    };
    area.addEventListener('contextmenu', onContext);
    return () => area.removeEventListener('contextmenu', onContext);
  }, [scrollAreaRef]);

  // Close on click-outside or Escape
  useEffect(() => {
    if (!menu) return;
    const close = (ev) => {
      if (menuRef.current && !menuRef.current.contains(ev.target)) setMenu(null);
    };
    const esc = (ev) => { if (ev.key === 'Escape') setMenu(null); };
    document.addEventListener('mousedown', close, true);
    document.addEventListener('keydown', esc, true);
    return () => {
      document.removeEventListener('mousedown', close, true);
      document.removeEventListener('keydown', esc, true);
    };
  }, [menu]);

  // Re-clamp y after the menu mounts and we know its height
  useEffect(() => {
    if (!menu || !menuRef.current) return;
    const h = menuRef.current.offsetHeight;
    const clampedY = clampY(menu.y, h);
    if (clampedY !== menu.y) setMenu((m) => m && { ...m, y: clampedY });
  });

  const dismiss = () => {
    setMenu(null);
    setShowChapters(false);
    setChapterSearch('');
  };

  const act = useCallback((fn) => {
    dismiss();
    // Small delay so the menu unmounts before execCommand runs
    setTimeout(fn, 0);
  }, []);

  // ── clipboard ────────────────────────────────────────────────────────────
  const doCut  = () => act(() => document.execCommand('cut'));
  const doCopy = () => act(() => document.execCommand('copy'));
  const doPaste = () => act(async () => {
    try {
      const text = await navigator.clipboard.readText();
      // Dispatch to the cross-paragraph selection hook; it handles both
      // single-para (insertText) and cross-para (React state update).
      window.dispatchEvent(new CustomEvent('loom:paste-replace', { detail: { text } }));
    } catch {
      document.execCommand('paste');
    }
  });

  // Select all paragraphs in the chapter (cross-paragraph via event)
  const doSelectAll = () => {
    dismiss();
    // Slight delay so the menu portal unmounts before the selection fires
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('loom:select-all-chapter'));
    }, 30);
  };

  // Delete the current selection (handles cross-paragraph ranges too)
  const doDelete = () => act(() => {
    window.dispatchEvent(new CustomEvent('loom:delete-selection'));
  });

  // ── chapter navigation ───────────────────────────────────────────────────
  const filteredChapters = useMemo(() => {
    const list = chapters || [];
    if (!chapterSearch.trim()) return list;
    const q = chapterSearch.toLowerCase();
    return list.filter((c) =>
      String(c.number).includes(q) || (c.title || '').toLowerCase().includes(q),
    );
  }, [chapters, chapterSearch]);

  const doGoToChapter = (chapterId) => {
    dismiss();
    setActiveChapterId(chapterId);
    // Note: do NOT dispatch loom:open-panel here — the editor is already open
    // (the user right-clicked inside it). Dispatching it would toggle the panel
    // closed via Workspace's toggle handler.
  };

  // ── tool shortcuts ───────────────────────────────────────────────────────
  const doCoach = () => act(() => {
    if (!activeChapterId) { toast.info('Open a chapter first.'); return; }
    window.dispatchEvent(new CustomEvent('loom:coach-trigger'));
  });

  const doToneShift = () => act(() => {
    if (!hasSelection() && typeof askToneShift === 'function') {
      toast.info('Select a passage first to shift its tone.');
      return;
    }
    if (typeof askToneShift === 'function') {
      askToneShift();
    } else {
      toast.info('Tone Shift is not available for this chapter.');
    }
  });

  const doFind = () => act(() => {
    window.dispatchEvent(new CustomEvent('loom:find-open'));
  });

  const doAnalyzeChapter = () => act(async () => {
    if (!activeChapterId) { toast.info('Open a chapter first.'); return; }
    const ch = chapters?.find((c) => c.id === activeChapterId) || chapters?.[0];
    if (!ch) { toast.info('No chapter loaded.'); return; }
    try {
      const { generateApi } = await import('@/lib/api');
      toast.promise(
        generateApi.analyzeChapter(ch.id),
        {
          loading: 'Analyzing chapter…',
          success: (d) => `Chapter analysed — Pacing: ${d.pacing ?? '?'}, Continuity: ${d.continuity ?? '?'}`,
          error:   (e) => e?.response?.data?.detail || 'Analysis failed.',
        },
      );
    } catch (e) {
      toast.error('Could not start analysis.');
    }
  });

  // ── AI panel shortcuts ───────────────────────────────────────────────────
  const doAskClaude = () => act(() => {
    // Switch to the chat panel in the activity bar
    window.dispatchEvent(new CustomEvent('loom:open-panel', { detail: 'chat' }));
    // Seed it with current chapter context
    const ch = chapters?.find((c) => c.id === activeChapterId);
    if (ch) {
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('loom:chat-seed-prompt', {
          detail: { prompt: `I'm currently working on Chapter ${ch.number}: "${ch.title || 'Untitled'}". ` },
        }));
      }, 150);
    }
  });

  const doVoiceCoach = () => act(() => {
    window.dispatchEvent(new CustomEvent('loom:open-voice-coach'));
  });

  const doFillLight = () => act(() => {
    window.dispatchEvent(new CustomEvent('loom:fill-light-open'));
  });

  // ── context & AI actions ────────────────────────────────────────────────
  const ch = chapters?.find((c) => c.id === activeChapterId) || chapters?.[0];

  const doRebuildChapter = () => act(async () => {
    if (!ch) { toast.info('Open a chapter first.'); return; }
    try {
      const { generateApi } = await import('@/lib/api');
      toast.promise(generateApi.analyzeChapter(ch.id), {
        loading: `Rebuilding context for Chapter ${ch.number}…`,
        success: () => { rebuild(); return `Chapter ${ch.number} context rebuilt.`; },
        error: (e) => e?.response?.data?.detail || 'Rebuild failed.',
      });
    } catch { toast.error('Could not rebuild chapter context.'); }
  });

  const doRebuildManuscript = () => act(() => {
    rebuild();
    toast.success('Full manuscript context rebuilt.');
  });

  const doContinuityChapter = () => act(async () => {
    if (!ch) { toast.info('Open a chapter first.'); return; }
    try {
      const { generateApi } = await import('@/lib/api');
      toast.promise(generateApi.analyzeChapter(ch.id), {
        loading: `Checking continuity in Chapter ${ch.number}…`,
        success: (d) => `Continuity checked — ${d?.continuity ?? 'no issues found'}.`,
        error: (e) => e?.response?.data?.detail || 'Continuity check failed.',
      });
    } catch { toast.error('Could not run continuity check.'); }
  });

  const doContinuityManuscript = () => act(() => {
    if (!project?.id) { toast.info('No project loaded.'); return; }
    // Delegate to EditorPanel's full continuity audit via event
    window.dispatchEvent(new CustomEvent('loom:continuity-audit'));
  });

  const doOpenBible = () => act(() => {
    window.dispatchEvent(new CustomEvent('loom:open-panel', { detail: 'bible' }));
  });

  const doOpenPlot = () => act(() => {
    window.dispatchEvent(new CustomEvent('loom:open-plot-builder'));
  });

  const doPolish = () => act(() => {
    window.dispatchEvent(new CustomEvent('loom:open-flow-pass'));
  });

  if (!menu) return null;

  // ── items ────────────────────────────────────────────────────────────────
  const separator = <div className="my-1 h-px bg-border" />;

  const Item = ({ icon: Icon, label, shortcut, onClick, disabled, ...rest }) => (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex w-full items-center gap-2.5 rounded-sm px-2.5 py-1.5 text-[12.5px] text-foreground hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:bg-primary/10"
      {...rest}
    >
      {Icon && <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
      <span className="flex-1 text-left">{label}</span>
      {shortcut && <kbd className="text-[10px] text-muted-foreground">{shortcut}</kbd>}
    </button>
  );

  return createPortal(
    <div
      ref={menuRef}
      style={{ position: 'fixed', top: menu.y, left: menu.x, width: MENU_WIDTH, zIndex: 9999 }}
      className="rounded-md border border-border bg-popover p-1 shadow-lg"
      data-testid="manuscript-context-menu"
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* ── Selection ── */}
      <Item icon={CheckSquare} label="Select All"  shortcut="⌘A" onClick={doSelectAll} data-testid="ctx-select-all" />

      {separator}

      {/* ── Clipboard ── */}
      <Item icon={Scissors}  label="Cut"    shortcut="⌘X" onClick={doCut}    disabled={!menu.hasText} />
      <Item icon={Copy}      label="Copy"   shortcut="⌘C" onClick={doCopy}   disabled={!menu.hasText} />
      <Item icon={Clipboard} label="Paste"  shortcut="⌘V" onClick={doPaste} />
      <Item icon={Trash2}    label="Delete" shortcut="⌫"  onClick={doDelete} disabled={!menu.hasText} data-testid="ctx-delete" />

      {separator}

      {/* ── Chapter Navigation ── */}
      <button
        type="button"
        onClick={() => { setShowChapters((s) => !s); setChapterSearch(''); }}
        data-testid="ctx-go-to-chapter"
        className="flex w-full items-center gap-2.5 rounded-sm px-2.5 py-1.5 text-[12.5px] text-foreground hover:bg-primary/10 focus:outline-none focus-visible:bg-primary/10"
      >
        <BookText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="flex-1 text-left">Go to Chapter</span>
        <ChevronRight className={cn('h-3 w-3 text-muted-foreground transition-transform', showChapters && 'rotate-90')} />
      </button>

      {showChapters && (
        <div className="mx-1 mt-0.5 mb-1 rounded border border-border bg-background/80" data-testid="ctx-chapter-list">
          {(chapters || []).length > 6 && (
            <div className="border-b border-border px-2 py-1.5">
              <input
                type="text"
                autoFocus
                placeholder="Search by number or title…"
                value={chapterSearch}
                onChange={(e) => setChapterSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                data-testid="ctx-chapter-search"
                className="w-full rounded bg-transparent text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none"
              />
            </div>
          )}
          <div className="max-h-44 overflow-y-auto py-0.5">
            {filteredChapters.length === 0 && (
              <p className="px-3 py-1.5 text-[12px] text-muted-foreground">No chapters match.</p>
            )}
            {filteredChapters.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => doGoToChapter(c.id)}
                data-testid={`ctx-chapter-row-${c.number}`}
                className={cn(
                  'flex w-full items-center gap-2 rounded-sm px-2.5 py-1 text-[12px] hover:bg-primary/10 transition-colors',
                  c.id === activeChapterId ? 'text-primary font-semibold' : 'text-foreground',
                )}
              >
                <span className="w-7 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground">{c.number}</span>
                <span className="flex-1 truncate text-left">{c.title || `Chapter ${c.number}`}</span>
                {c.id === activeChapterId && <Check className="h-2.5 w-2.5 shrink-0 text-primary" />}
              </button>
            ))}
          </div>
        </div>
      )}

      {separator}

      {/* ── Context & AI ── */}
      <Item icon={RefreshCw}  label="Rebuild chapter context"     onClick={doRebuildChapter}     data-testid="ctx-rebuild-chapter" />
      <Item icon={RefreshCw}  label="Rebuild full manuscript"     onClick={doRebuildManuscript}  data-testid="ctx-rebuild-manuscript" />
      <Item icon={ScanSearch} label="Continuity: this chapter"    onClick={doContinuityChapter}  data-testid="ctx-continuity-chapter" />
      <Item icon={ScanSearch} label="Continuity: full manuscript" onClick={doContinuityManuscript} data-testid="ctx-continuity-manuscript" />
      <Item icon={Wand2}      label="Polish for readability"      onClick={doPolish}             data-testid="ctx-polish" />
      <Item icon={Users}      label="Open Character Bible"        onClick={doOpenBible}          data-testid="ctx-open-bible" />
      <Item icon={Map}        label="Open Plot Panel"             onClick={doOpenPlot}           data-testid="ctx-open-plot" />

      {separator}

      {/* ── Tool shortcuts ── */}
      <Item icon={Search}    label="Find in manuscript"    shortcut="⌘F"  onClick={doFind} />
      <Item icon={Sparkles}  label="Coach: next beat"                     onClick={doCoach} />
      <Item icon={Zap}       label="Tone shift selection"                  onClick={doToneShift} disabled={!menu.hasText} />
      <Item icon={BarChart2} label="Analyse this chapter"                  onClick={doAnalyzeChapter} />

      {separator}

      {/* ── AI tools ── */}
      <Item icon={MessageSquare} label="Ask Claude"                        onClick={doAskClaude} data-testid="ctx-ask-claude" />
      <Item icon={Mic2}          label="Voice Coach"                       onClick={doVoiceCoach} data-testid="ctx-voice-coach" />
      <Item icon={Zap}           label="Fill Light this chapter"           onClick={doFillLight} data-testid="ctx-fill-light" />
    </div>,
    document.body,
  );
}
